import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { LocationOn, School, Star, EmojiEvents, MenuBook } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import GradientSurface from './shared/GradientSurface';
import { useReducedMotionPreferred } from '../hooks/useReducedMotionPreferred';

function InstituteCard({ institute }) {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotionPreferred();
  const [imageError, setImageError] = React.useState(false);

  const placeholderImages = [
    'https://picsum.photos/seed/institute1/800/600',
    'https://picsum.photos/seed/institute2/800/600',
    'https://picsum.photos/seed/institute3/800/600',
    'https://picsum.photos/seed/institute4/800/600',
    'https://picsum.photos/seed/institute5/800/600',
    'https://picsum.photos/seed/institute6/800/600',
    'https://picsum.photos/seed/institute7/800/600',
    'https://picsum.photos/seed/institute8/800/600',
  ];

  const getPlaceholderUrl = () => {
    const hash = institute.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return placeholderImages[hash % placeholderImages.length];
  };

  const getImageUrl = () => {
    if (imageError) return getPlaceholderUrl();
    if (institute.gallery && institute.gallery.length > 0) {
      const imagePath = institute.gallery[0];
      if (imagePath.startsWith('/uploads/')) {
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${baseUrl}${imagePath}`;
      }
      return imagePath;
    }
    return getPlaceholderUrl();
  };

  const hoverProps = reducedMotion
    ? {}
    : { whileHover: { y: -4, boxShadow: '0 16px 40px rgba(0,0,0,0.16)' } };

  return (
    <motion.div
      {...hoverProps}
      transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      style={{ height: '100%' }}
    >
      <GradientSurface
        variant="surface"
        onClick={() => navigate(`/institutes/${institute.id}`)}
        sx={{
          cursor: 'pointer',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Cover image — 16/10 aspect ratio */}
        <Box
          sx={{
            position: 'relative',
            aspectRatio: '16/10',
            overflow: 'hidden',
            bgcolor: 'background.subtle',
          }}
        >
          <Box
            component="img"
            src={getImageUrl()}
            alt={institute.name}
            onError={() => setImageError(true)}
            loading="lazy"
            decoding="async"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />

          {/* Status badge */}
          <Chip
            label={institute.active ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              bgcolor: institute.active ? 'success.main' : 'action.disabled',
              color: 'common.white',
              fontWeight: 700,
              backdropFilter: 'blur(10px)',
            }}
          />

          {/* Rating overlay */}
          {institute.rating && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 10,
                left: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              <Star sx={{ fontSize: 16, color: 'warning.main' }} />
              <Typography variant="body2" fontWeight={700} color="text.primary">
                {institute.rating.toFixed(1)}
              </Typography>
            </Box>
          )}

          {/* Success rate badge */}
          {institute.success_rate && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'primary.main',
                backdropFilter: 'blur(10px)',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              <EmojiEvents sx={{ fontSize: 14, color: 'common.white' }} />
              <Typography variant="body2" fontWeight={700} color="common.white">
                {institute.success_rate}%
              </Typography>
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flexGrow: 1, p: 2.5 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              fontSize: '1rem',
            }}
          >
            {institute.name}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
            <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.5,
              }}
            >
              {institute.city ? `${institute.city}` : ''}
              {institute.address && ` • ${institute.address.substring(0, 50)}${institute.address.length > 50 ? '...' : ''}`}
            </Typography>
          </Box>

          {/* Courses */}
          {institute.courses_offered && institute.courses_offered.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                <MenuBook sx={{ fontSize: 14, color: 'primary.main' }} />
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  Courses
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {institute.courses_offered.slice(0, 3).map((course, idx) => (
                  <Chip
                    key={idx}
                    label={course}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      bgcolor: 'primary.soft',
                      color: 'primary.dark',
                      fontWeight: 500,
                      border: 'none',
                    }}
                  />
                ))}
                {institute.courses_offered.length > 3 && (
                  <Chip
                    label={`+${institute.courses_offered.length - 3}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.68rem',
                      bgcolor: 'background.subtle',
                      fontWeight: 600,
                      color: 'text.secondary',
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Students row */}
          {institute.total_students && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <School sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                {institute.total_students}+ Students
              </Typography>
            </Box>
          )}
        </Box>
      </GradientSurface>
    </motion.div>
  );
}

export default InstituteCard;
